/** The MOGZO API. Production default needs no build setting; override with `VITE_API_URL`. */
export const API_URL: string =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD ? 'https://api.mogzo.com' : 'http://localhost:3000');

const REQUEST_TIMEOUT_MS = 15_000;
const SESSION_TIMEOUT_MS = 8_000;

export interface Me {
  id: string;
  email: string;
  emailVerified: boolean;
  /** The one public name. */
  username: string;
  /** A copy of the username. */
  displayName: string;
  image: string | null;
  roles: string[];
  twoFactorEnabled: boolean;
  ageBand: 'teen' | 'adult' | null;
  profileComplete: boolean;
  /** When the username can next change; null if it can now. */
  usernameChangeableAt: string | null;
  /** `credential` (email and password), `google`, `discord`. */
  signInMethods: string[];
  createdAt: string;
}

export interface UsernameCheck {
  status: 'free' | 'taken' | 'invalid' | 'yours';
  message: string | null;
  /** Free names like a taken one. */
  suggestions: string[];
}

export interface PublicConfig {
  socialProviders: ('google' | 'discord')[];
  turnstileSiteKey: string | null;
}

/** Our own API's error shape (`/v1/*` routes). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(API_URL + path, {
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    ...init,
    credentials: 'include',
    headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), ...init.headers },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = body?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Something went wrong. Try again.',
    );
  }
  return body as T;
}

/** The signed-in user, or `null` when signed out. */
export async function fetchMe(): Promise<Me | null> {
  // better-auth answers 200 with `null` when signed out, so visitors don't log 401 errors.
  const session = await fetch(`${API_URL}/auth/get-session`, {
    credentials: 'include',
    // If the API is struggling, show the signed-out state instead of waiting forever.
    signal: AbortSignal.timeout(SESSION_TIMEOUT_MS),
  })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
  if (!session) return null;
  try {
    return await apiFetch<Me>('/v1/me');
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function fetchPublicConfig(): Promise<PublicConfig> {
  return apiFetch<PublicConfig>('/v1/config');
}

export function checkUsername(name: string, signal?: AbortSignal): Promise<UsernameCheck> {
  return apiFetch(`/v1/usernames/check?name=${encodeURIComponent(name)}`, { signal });
}

/** A made-up username nobody has ("Pick another"). */
export function randomUsername(): Promise<{ username: string }> {
  return apiFetch('/v1/usernames/random');
}

export function changeUsername(username: string): Promise<Me> {
  return apiFetch<Me>('/v1/me/username', { method: 'POST', body: JSON.stringify({ username }) });
}

/** Sends a link to the new address; the email changes once it's opened. */
export function changeEmail(newEmail: string, password?: string): Promise<{ ok: true }> {
  return apiFetch('/v1/me/email', {
    method: 'POST',
    body: JSON.stringify({ newEmail, ...(password ? { password } : {}) }),
  });
}

export function submitBirthMonth(birthMonth: string): Promise<Me> {
  return apiFetch<Me>('/v1/me/birth-month', {
    method: 'POST',
    body: JSON.stringify({ birthMonth }),
  });
}
